import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SoundGate from "@/components/clientDemo/tour/SoundGate";
import axios from "axios";
import { API } from "@/lib/auth";
import { DemoModeProvider } from "@/lib/demoMode";
import { GuidedTourProvider, useGuidedTour } from "@/lib/guidedTour";
import { DemoBrandProvider } from "@/lib/demoBrand";
import TourControlStrip from "@/components/clientDemo/tour/TourControlStrip";
import TourOverlay from "@/components/clientDemo/tour/TourOverlay";
import { EXCHANGE_TOUR_SCRIPT, exchangeAudioUrl } from "@/components/clientDemo/exchange/exchangeTourScript";
import ExchangeHero from "@/components/clientDemo/exchange/ExchangeHero";
import ExchangeIntro from "@/components/clientDemo/exchange/ExchangeIntro";
import ExchangeGovSection from "@/components/clientDemo/exchange/ExchangeGovSection";
import ExchangeChurchSection from "@/components/clientDemo/exchange/ExchangeChurchSection";
import ExchangeCommerceSection from "@/components/clientDemo/exchange/ExchangeCommerceSection";
import ExchangePillarsSection from "@/components/clientDemo/exchange/ExchangePillarsSection";
import ExchangeIntakeSection from "@/components/clientDemo/exchange/ExchangeIntakeSection";
import ExchangeSanctuarySection from "@/components/clientDemo/exchange/ExchangeSanctuarySection";
import ExchangeCareerSection from "@/components/clientDemo/exchange/ExchangeCareerSection";
import ExchangeClearingSection from "@/components/clientDemo/exchange/ExchangeClearingSection";
import ExchangeJourneySection from "@/components/clientDemo/exchange/ExchangeJourneySection";
import ExchangePilotSection from "@/components/clientDemo/exchange/ExchangePilotSection";
import HypeCard from "@/components/clientDemo/intro/HypeCard";
import TourCuePointer from "@/components/clientDemo/tour/TourCuePointer";
import ExchangeOrgVisuals from "@/components/clientDemo/exchange/ExchangeOrgVisuals";
import ExchangeStage, { TRANSITION_MS } from "@/components/clientDemo/exchange/ExchangeStage";
import ExchangeOutro from "@/components/clientDemo/exchange/ExchangeOutro";
import TourMusicBed from "@/components/clientDemo/tour/TourMusicBed";
import { AnimatePresence } from "framer-motion";

const BASE = process.env.REACT_APP_BACKEND_URL;
const TEA_TOUR_COLORS = { ring: "212, 175, 55", ring2: "244, 216, 138", arrow: "#D4AF37" };
const SECTIONS = {
  beat02: ExchangeGovSection, church: ExchangeChurchSection, commerce: ExchangeCommerceSection,
  pillars: ExchangePillarsSection, beat03: ExchangeIntakeSection, beat04: ExchangeSanctuarySection,
  beat05: ExchangeCareerSection, beat06: ExchangeClearingSection, beat07: ExchangeJourneySection,
  pilot: ExchangePilotSection,
};

// Personal-link visitors: start the auto tour the moment the intro ends.
function AutoStarter({ go, onStarted }) {
  const { start } = useGuidedTour();
  useEffect(() => { if (go) { start("auto", 0); onStarted(); } }, [go]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

export default function ExchangeAlliancePage({ slug = "exchange-alliance", businessName, embed = false, autoStart = false }) {
  const [config, setConfig] = useState(null);
  const [lines, setLines] = useState({});
  const [intro, setIntro] = useState(null);
  const [showIntro, setShowIntro] = useState(true);
  const [heroKey, setHeroKey] = useState(0);
  const [showHype, setShowHype] = useState(false);
  const [showOutro, setShowOutro] = useState(false);
  const autoRef = useRef(autoStart || new URLSearchParams(window.location.search).get("auto") === "1");
  const [autoGo, setAutoGo] = useState(false);
  const shareToken = useMemo(() => { try { return JSON.parse(sessionStorage.getItem(`demo_share_${slug}`) || "null")?.token; } catch { return null; } }, [slug]);
  const playOutro = useCallback(() => { setShowHype(false); setShowOutro(true); }, []);
  const onTourComplete = useCallback(() => {
    playOutro();
    if (shareToken) axios.post(`${API}/share/${shareToken}/event`, { kind: "finished" }).catch(() => {});
  }, [playOutro, shareToken]);
  const [musicOn, setMusicOn] = useState(() => { try { return localStorage.getItem("demo_tour_music") !== "off"; } catch { return true; } });
  useEffect(() => { try { localStorage.setItem("demo_tour_music", musicOn ? "on" : "off"); } catch {} }, [musicOn]);
  const sfx = (name) => `${BASE}/api/static/audio/${slug}-${name}.mp3`;
  const closeHype = useCallback(() => setShowHype(false), []);
  useEffect(() => {
    axios.get(`${API}/demos/${slug}/intro`).then(({ data }) => setIntro(data)).catch(() => setShowIntro(false));
  }, [slug]);
  const [isAdmin] = useState(() => { if (embed) return false; try { return !!localStorage.getItem("nex_token"); } catch { return false; } });

  useEffect(() => {
    axios.get(`${API}/demos/${slug}/config`).then(({ data }) => setConfig(data)).catch(() => {});
  }, [slug]);
  const loadLines = useCallback(() => axios.get(`${API}/demos/${slug}/tour-lines`)
    .then(({ data }) => setLines(Object.fromEntries(data.items.map((l) => [l.step, l])))).catch(() => {}), [slug]);
  useEffect(() => { loadLines(); }, [loadLines]);
  const onLineSaved = (line) => setLines((m) => ({ ...m, [line.step]: { ...m[line.step], ...line } }));

  const name = config?.business_name || businessName || "The Exchange Alliance";
  const accent = config?.accent_color || "#D4AF37";
  const script = useMemo(() => EXCHANGE_TOUR_SCRIPT.map((b) => {
    const l = lines[b.step];
    return {
      ...b,
      caption: l?.text || b.caption,
      edited: l?.edited,
      silent: !!l?.placeholder,
      audio: l?.rendered && !l?.placeholder ? exchangeAudioUrl(slug, b.step, l.version) : null,
    };
  }), [lines, slug]);
  const brand = useMemo(() => ({ slug, name, accent, isAdmin, tourColors: TEA_TOUR_COLORS }), [slug, name, accent, isAdmin]);
  const inLine = lines.IN;
  const jumps = useMemo(() => script.map((b) => [b.id, b.chip, b.silent]), [script]);
  const [view, setView] = useState(0);
  const viewRef = useRef(0);
  viewRef.current = view;
  const getLeadInMs = useCallback((s) => (s === viewRef.current ? 0 : TRANSITION_MS), []);
  const logoUrl = intro?.logo_url ? `${BASE}${intro.logo_url}` : null;
  const renderBeat = (id) => {
    if (id === "beat01") return <ExchangeHero key={heroKey} name={name} accent={accent} beats={script} logoUrl={logoUrl} />;
    const Section = SECTIONS[id];
    return Section ? <Section /> : null;
  };

  return (
    <DemoBrandProvider value={brand}>
      <DemoModeProvider value>
        <GuidedTourProvider script={script} getLeadInMs={getLeadInMs} onComplete={onTourComplete}>
          <AutoStarter go={autoGo} onStarted={() => { setAutoGo(false); window.history.replaceState(null, "", window.location.pathname); }} />
          <TourMusicBed url={showOutro ? null : sfx("music-bed")} enabled={musicOn} />
          <TourControlStrip jumps={jumps} isAdmin={isAdmin} onRerecorded={loadLines} onReplayIntro={() => { setShowHype(false); setShowOutro(false); setShowIntro(true); }} onPlayFinale={playOutro} pulseStart={showHype} musicOn={musicOn} onToggleMusic={() => setMusicOn((m) => !m)} />
          <ExchangeStage script={script} view={view} setView={setView} renderBeat={renderBeat} swooshOut={sfx("swoosh-out")} swooshIn={sfx("swoosh-in")} sfxOn={musicOn} />
          {showHype && !showIntro && lines.HY && (
            <HypeCard
              key={lines.HY.version}
              line={lines.HY}
              audioUrl={lines.HY.rendered ? exchangeAudioUrl(slug, "HY", lines.HY.version) : null}
              isAdmin={isAdmin}
              onLineSaved={onLineSaved}
              onClose={closeHype}
              persist
              desktopOnly
            />
          )}
          {showIntro && intro && Object.keys(lines).length > 0 && (
            <ExchangeIntro
              logoUrl={intro.logo_url ? `${BASE}${intro.logo_url}` : null}
              musicUrl={`${BASE}/api/static/audio/${slug}-intro-swell.mp3`}
              narrationUrl={inLine?.rendered ? exchangeAudioUrl(slug, "IN", inLine.version) : null}
              onClose={() => { setShowIntro(false); setHeroKey((k) => k + 1); if (autoRef.current) { autoRef.current = false; setAutoGo(true); } else setShowHype(true); }}
            />
          )}
          <TourOverlay isAdmin={isAdmin} onLineSaved={onLineSaved} autoScroll={false} bottomBarBelow={1280} hideTextBelow={1024} />
          <SoundGate color="#D4AF37" />
          <AnimatePresence>
            {showOutro && (
              <ExchangeOutro
                logoUrl={logoUrl}
                musicUrl={sfx("outro-swell")}
                narrationUrl={lines.OU?.rendered ? exchangeAudioUrl(slug, "OU", lines.OU.version) : null}
                onClose={() => setShowOutro(false)}
                onReplay={() => { setShowOutro(false); setTimeout(() => setShowOutro(true), 700); }}
              />
            )}
          </AnimatePresence>
          <TourCuePointer />
          <ExchangeOrgVisuals />
        </GuidedTourProvider>
      </DemoModeProvider>
    </DemoBrandProvider>
  );
}
